import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useSetReturnMode } from "@babackapp/return-widget-components/hooks/use-set-return-mode";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useAvailableReturnModes } from "./use-available-return-modes";
import { useWidgetParams } from "./use-widget-params";
import type { ReturnMode } from "@babackapp/return-widget-components/types";

const returnModeSchema = z
  .object({
    returnMode: z.string(),
    pickupDate: z.string().optional(),
    storeId: z.string().optional(),
    isPickup: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.returnMode === "carrier" && data.isPickup && !data.pickupDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pickupDate"],
        message: "required",
      });
    }
    if (data.returnMode === "store" && !data.storeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["storeId"],
        message: "required",
      });
    }
  });

type ReturnModeFormValues = {
  returnMode: ReturnMode;
  pickupDate?: string;
  storeId?: string;
  isPickup?: boolean;
};

export function useReturnModeForm() {
  const navigate = useNavigate();
  const { shopId, orderId } = useWidgetParams();
  const { data: shop } = usePublicShop(shopId);
  const { data: order } = usePublicOrder(orderId);
  const setReturnMode = useSetReturnMode(orderId);
  const { availableReturnModes, selectableStores, hasStoreReturns } =
    useAvailableReturnModes();

  const isPickup = order?.isPickup === true;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultPickupDate = tomorrow.toISOString().split("T")[0];

  const firstAvailableMode = availableReturnModes[0];
  const form = useForm<ReturnModeFormValues>({
    resolver: zodResolver(returnModeSchema),
    defaultValues: {
      returnMode: firstAvailableMode,
      pickupDate: isPickup ? defaultPickupDate : undefined,
      storeId: undefined,
      isPickup,
    },
  });

  // The selected mode always belongs to the offered ones: a block that leaves
  // the screen once the order has loaded must not stay selected (BBS-385).
  const selectedMode = form.watch("returnMode");
  const isSelectedModeOffered = availableReturnModes.includes(selectedMode);
  useEffect(() => {
    if (!isSelectedModeOffered) form.setValue("returnMode", firstAvailableMode);
  }, [form, isSelectedModeOffered, firstAvailableMode]);

  const onSubmit = form.handleSubmit(async (values) => {
    setReturnMode(
      values.returnMode,
      values.returnMode === "store" ? (values.storeId ?? null) : null,
      values.returnMode === "carrier" ? (values.pickupDate ?? null) : null,
    );
    navigate("../address");
  });

  return {
    form,
    onSubmit,
    availableReturnModes,
    selectableStores,
    hasStoreReturns,
    isPickup,
    userPortalTexts: shop?.preferences?.userPortal?.carrierSelectionMessage,
  };
}
